/**
 * ThumbnailObserver is currently shared by views that contain a list of thumbnails (e.g projects and screens list),
 * assuming that when the whole app becomes an SPA, only one specified container (e.g. <main></main>) for IntersectionObserver is used for all consumers.
 *
 * @typedef ThumbnailObserver
 * @property {function(HTMLElement, function(IntersectionObserverEntry))} observe Observes given element, calls callback on intersection
 * @property {function(HTMLElement)} unobserve Unobserves given element
 */

/** @type {IntersectionObserver} */
let intersectionObserver;

/** @type {ThumbnailObserver} */
let thumbnailObserver;

const callbacks = new WeakMap();
function handleIntersection(entries) {
    entries.forEach(entry => {
        const callback = callbacks.get(entry.target);
        if (callback) {
            callback(entry);
        }
    });
}

/**
 * Returns singleton instance of thumbnailObserver
 * @param {string} containerSelector
 * @returns {thumbnailObserver}
 */
export function getThumbnailObserver(container) {
    if (!thumbnailObserver) {
        intersectionObserver = new IntersectionObserver(handleIntersection, {
            root: typeof container === "string" ? document.querySelector(container) : container,
            threshold: [0, 0.01]
        });

        thumbnailObserver = {
            observe(target, callback) {
                callbacks.set(target, callback);
                intersectionObserver.observe(target);
            },
            unobserve(target) {
                intersectionObserver.unobserve(target);
                callbacks.delete(target);
            }
        };
    }

    return thumbnailObserver;
}
